import type {CircleIntersectionDefinition2D} from './CircleCircleIntersectionSolver';
import {ImplicitConicTool2D} from './ImplicitConicTool2D';
import {ImplicitParametricCurveIntersector2D} from './ImplicitParametricCurveIntersector2D';
import {ParametricSplineCurveAdaptor2D} from './ParametricSplineCurveAdaptor2D';

/**
 * Puppy CAD 内核 语义二维完整圆—Bezier/B-Spline 求交门面。
 *
 * 直接使用 Puppy CAD 内核 的径向隐式函数 `sqrt(dx²+dy²)-r`、原生参数采样、极值相切恢复和
 * 容差零区间，不再构造齐次 Bernstein 圆多项式。
 */
export class CircleSplineIntersectionSolver {
    /** Puppy CAD 内核 等价公共求交器。 */
    private readonly intersector: ImplicitParametricCurveIntersector2D;

    /**
     * 创建完整圆—样条求交门面。
     * @param intersector Puppy CAD 内核 等价公共求交器。
     */
    public constructor(
        intersector: ImplicitParametricCurveIntersector2D
            = new ImplicitParametricCurveIntersector2D(),
    ) {
        this.intersector = intersector;
    }

    /**
     * 求解完整圆与参数样条。
     * @param circle 完整圆定义。
     * @param curve Bezier/B-Spline 定义。
     * @param options Puppy CAD 内核 双容差选项。
     * @returns 离散交点和容差重合段。
     */
    public solve(
        circle: CircleIntersectionDefinition2D,
        curve: LineSplineCurveDefinition2D,
        options?: CircleSplineIntersectionOptions,
    ): CircleSplineIntersectionResult2D {
        const adaptor = new ParametricSplineCurveAdaptor2D(curve);
        const result = this.intersector.perform(
            new ImplicitConicTool2D({
                kind: 'circle',
                center: circle.center,
                radius: circle.radius,
            }),
            adaptor,
            {
                confusionTolerance: options?.confusionTolerance ?? options?.distanceTolerance,
                distanceTolerance: options?.distanceTolerance,
            },
        );
        const intervalBoundaries = adaptor.resolveC1IntervalBoundaries();
        return {
            done: result.done,
            error: result.error,
            points: result.points.map((point) => ({
                point: point.point,
                circleAngle: point.implicitParameter,
                curveParameter: point.curveParameter,
                spanIndex: this.resolveIntervalIndex(intervalBoundaries, point.curveParameter),
                kind: point.implicitTransition.type === 'touch'
                    ? 'tangent'
                    : point.implicitTransition.type === 'undecided'
                        ? 'undetermined'
                        : 'simple',
                atCircleSeam: point.implicitTransition.position !== 'middle',
                atCurveEndpoint: point.curveTransition.position !== 'middle',
                circleTransition: point.implicitTransition,
                curveTransition: point.curveTransition,
            })),
            overlaps: result.segments.map((segment) => {
                const coversFullCurve = Math.abs(segment.firstEndpoint.curveParameter - adaptor.firstParameter) < 1e-8
                    && Math.abs(segment.secondEndpoint.curveParameter - adaptor.lastParameter) < 1e-8;
                const implicitSpan = Math.abs(
                    segment.secondEndpoint.implicitParameter - segment.firstEndpoint.implicitParameter,
                );
                return {
                    startPoint: segment.firstEndpoint.point,
                    endPoint: segment.secondEndpoint.point,
                    startCircleAngle: segment.firstEndpoint.implicitParameter,
                    endCircleAngle: segment.secondEndpoint.implicitParameter,
                    startCurveParameter: segment.firstEndpoint.curveParameter,
                    endCurveParameter: segment.secondEndpoint.curveParameter,
                    spanIndex: this.resolveIntervalIndex(
                        intervalBoundaries,
                        segment.firstEndpoint.curveParameter,
                    ),
                    direction: segment.opposite ? 'clockwise' as const : 'counterclockwise' as const,
                    coversFullCircle: coversFullCurve && implicitSpan >= Math.PI * 2 - 1e-8,
                    coversFullCurve,
                    firstCircleTransition: segment.firstEndpoint.implicitTransition,
                    firstCurveTransition: segment.firstEndpoint.curveTransition,
                    secondCircleTransition: segment.secondEndpoint.implicitTransition,
                    secondCurveTransition: segment.secondEndpoint.curveTransition,
                };
            }),
        };
    }

    /** 根据 C1 区间边界解析稳定复合区间索引。 */
    private resolveIntervalIndex(boundaries: readonly number[], parameter: number): number {
        const index = boundaries.findIndex((_boundary, boundaryIndex) => (
            boundaryIndex < boundaries.length - 1 && parameter <= boundaries[boundaryIndex + 1]! + 1e-10
        ));
        return Math.max(0, index);
    }
}
